import * as S from "./Styles";
import { collection, doc, onSnapshot, orderBy, query, Timestamp, writeBatch } from 'firebase/firestore';
import { COLLECTIONS, COLORS } from '../../constants';
import { db } from '../../firebase/firebaseConfig';
import { useState, useEffect } from 'react';
import { ErrorDisplay } from '../Displays';
import { IProduct } from '../../domains/Product';
import { AccessMap, AccessPeriod, UserProfile } from '../../pages/CheckoutPage/userProfileSlice';
import { Button } from "../Buttons";
import { isGrade, Grade } from "../../types/App";
import { ActivationMessage, Mail, MailConverter } from "../../domains/Mail";
import { PDF_PACKAGES } from "../../domains/Product/Product";

export const OrderTable = () => {
    const [orderData, setOrderData] = useState<Order[] | null>(null);
    const [error, setError] = useState<Error | null>(null);
    useEffect(() => {
        if (orderData === null) {
            const q = query(collection(db, COLLECTIONS.orders), orderBy("orderId", "desc"));
            const unsub = onSnapshot(q,
                (querySnapshot) => {
                    const orders: Order[] = []
                    querySnapshot.forEach((doc) => {
                        if (doc.id !== "meta") {
                            orders.push(doc.data() as Order)
                        }
                    });
                    orders.sort((o1, o2) => o2.orderId - o1.orderId);
                    setOrderData(orders);
                },
                (error) => setError(error));
            return unsub;
        }
    }, [orderData, setOrderData]);

    return (
        <>
            <S.Table backgroundColor={COLORS.DIDACTO_WHITE} nRows={12} width="95vw" fontSize="1.5rem">
                <S.HeaderCell justifyContent="center">#</S.HeaderCell>
                <S.HeaderCell justifyContent="center">Datum</S.HeaderCell>
                <S.HeaderCell justifyContent="center">Positionen</S.HeaderCell>
                <S.HeaderCell justifyContent="center">Preis</S.HeaderCell>
                <S.HeaderCell justifyContent="center">Name</S.HeaderCell>
                <S.HeaderCell justifyContent="center">Email</S.HeaderCell>
                <S.HeaderCell justifyContent="center">Schule</S.HeaderCell>
                <S.HeaderCell justifyContent="center">Adresse</S.HeaderCell>
                <S.HeaderCell justifyContent="center">Zugriffe</S.HeaderCell>
                <S.HeaderCell justifyContent="center">Verifziert</S.HeaderCell>
                <S.HeaderCell justifyContent="center">Erledigt</S.HeaderCell>
                <S.HeaderCell justifyContent="center">Bestellung genehmigen</S.HeaderCell>
                {orderData ? orderData.map(order => <OrderTableRow key={order.orderId} {...order} />) : null}
            </S.Table>
            {error ? <ErrorDisplay error={error} /> : null}
        </>

    )
}

type Order = {
    id: string,
    orderId: number,
    positions: IProduct[],
    userProfile: UserProfile,
    totalPrice: number,
    completed: boolean,
    createdAt?: Timestamp,
}

const OrderTableRow = ({ id, orderId, createdAt, positions, userProfile, totalPrice, completed }: Order) => {
    const { uid, email, firstName, lastName, school, street, postalCode, phone } = userProfile;
    const [_userProfile, _setUserProfile] = useState<UserProfile | undefined>(undefined);
    useEffect(() => {
        const getUserDoc = async () => {
            try {
                const unsub = onSnapshot(doc(db, COLLECTIONS.users, uid), (userDoc) => {
                    const userProfileData = userDoc.data();
                    if (!userProfileData) {
                        throw new Error(`User profile für ${uid}/${email} existiert nicht`);
                    } else {
                        _setUserProfile(userProfileData as UserProfile);
                    }
                });
                return unsub;
            } catch (error) {
                _setUserProfile(undefined);
                console.error(error);
            }
        }
        getUserDoc();
    }, [uid, _setUserProfile, email]);
    if (!_userProfile) {
        return (
            <div style={{ gridColumn: "1/13j" }}>
                <ErrorDisplay error={new Error(`User profile für ${uid}/${email} existiert nicht`)} />
            </div>
        )
    } else {
        const fmtCreationDate = createdAt ? 
            createdAt.toDate().toLocaleDateString("de-DE") : 
            "";
        let {pdfAccess, wpAccess, examAccess} = _userProfile
        return (
            <>
                <S.Cell data-orderid={id} justifyContent="center">{orderId}</S.Cell>
                <S.Cell data-orderid={id} justifyContent="center">{fmtCreationDate}</S.Cell>
                <S.Cell data-orderid={id} justifyContent="center"><PositionList positions={positions} /></S.Cell>
                <S.Cell data-orderid={id} justifyContent="center">{`${totalPrice}.-`}</S.Cell>
                <S.Cell data-orderid={id} justifyContent="center">{`${firstName} ${lastName}`}</S.Cell>
                <S.Cell data-orderid={id} justifyContent="center">{email}</S.Cell>
                <S.Cell data-orderid={id} justifyContent="center">{school}</S.Cell>
                <S.Cell data-orderid={id} justifyContent="center">
                    <div style={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                        <div >{street}</div>
                        <div>{postalCode}</div>
                        <div>{phone}</div>
                    </div>
                </S.Cell>
                <S.Cell data-orderid={id} justifyContent="center">
                    <div style={{ display: "flex", alignItems: "flex-start", flexDirection: "column", fontSize: "0.7rem" }}>
                        <pre>{JSON.stringify({pdfAccess, wpAccess, examAccess}, null, 2)}</pre>
                    </div>
                </S.Cell>
                <S.Cell data-orderid={id} justifyContent="center">
                    <S.Checkbox type="checkbox" checked={_userProfile?.isVerifiedTeacher} disabled={true} />
                </S.Cell>
                <S.Cell data-orderid={id} justifyContent="center">
                    <S.Checkbox type="checkbox" disabled={true} checked={completed} />
                </S.Cell>
                <S.Cell data-orderid={id} justifyContent="center" textAlign="center">
                    <Button id="approve-order"
                        variant="blue"
                        style={{ fontSize: "1.5rem", padding: "0.2rem 1rem", marginRight: "1rem" }}
                        type="reset"
                        text="Berechtigen"
                        onClick={() => authorizeUserForProducts(_userProfile, positions, id)}
                        disabled={_userProfile.isVerifiedTeacher && completed} />
                </S.Cell>

            </>
        )
    }
}


const PositionList = ({ positions }: { positions: IProduct[] }) => {
    return (
        <ol style={{ listStyleType: "none", margin: "0", padding: "0" }}>
            {(positions as any[]).map((position, i) => {
                return (
                    <li key={i}>{position.productId} {position.name}</li>
                )
            })}
        </ol>
    )
}

const authorizeUserForProducts = async (userProfile: UserProfile, products: IProduct[], id: string) => {
    try {
        const pdfAccess = new Set(userProfile.pdfAccess);
        // Local copies are only used to compute extended validity ranges
        // (e.g. two products in this order granting the same grade) - the
        // actual write below targets each grade's own field path, so it
        // can't clobber a grade that a concurrent order/write touches.
        const examAccess = userProfile.examAccess ?? {};
        const wpAccess = userProfile.wpAccess ?? {};
        const accessUpdates: { [fieldPath: string]: unknown } = {};
        products.forEach(product => {
            product.wpAccess.forEach(accessVal => {
                if (isGrade(accessVal)) {
                    const validRange = calcWpRange(accessVal, wpAccess);
                    const newAccess = {
                        triggerRefresh: true,
                        validFrom: {
                            seconds: validRange.validFrom.seconds,
                            nanoseconds: validRange.validFrom.nanoseconds,
                        },
                        validTo :{
                            seconds: validRange.validTo.seconds,
                            nanoseconds: validRange.validTo.nanoseconds,
                        }
                    };
                    wpAccess[accessVal] = newAccess;
                    accessUpdates[`wpAccess.${accessVal}`] = newAccess;
                } else {
                    throw new Error(`Invalid access value found in wpAccess-array for product ${product}`);
                }
            });
            product.examAccess.forEach(accessVal => {
                if (isGrade(accessVal)) {
                    const validRange = calcExamRange(accessVal, examAccess);
                    const newAccess = {
                        triggerRefresh: true,
                        validFrom: {
                            seconds: validRange.validFrom.seconds,
                            nanoseconds: validRange.validFrom.nanoseconds,
                        },
                        validTo :{
                            seconds: validRange.validTo.seconds,
                            nanoseconds: validRange.validTo.nanoseconds,
                        }
                    };
                    examAccess[accessVal] = newAccess;
                    accessUpdates[`examAccess.${accessVal}`] = newAccess;
                } else {
                    throw new Error(`Invalid access value found in examAccess-array for product ${product}`);
                }
            });
            product.pdfAccess.forEach(accessVal => {
                if (Object.keys(PDF_PACKAGES).includes(accessVal)) {
                    pdfAccess.add(accessVal);
                } else {
                    throw new Error(`Invalid access value found in pdfAccess-array for product ${product}`);
                }
            })
        });
        const userProfileRef = doc(db, COLLECTIONS.users, userProfile.uid);
        const orderRef = doc(db, COLLECTIONS.orders, id);
        const mailRef = doc(collection(db, COLLECTIONS.mail));
        const message = new ActivationMessage();
        const mail = new Mail({ to: userProfile.email, bcc: "", message });
        const batch = writeBatch(db);
        // update() (not set/merge) so wpAccess.<grade>/examAccess.<grade> are
        // written as targeted field paths - a concurrent order for the same
        // user touching a different grade can no longer overwrite this one.
        batch.update(userProfileRef, {
            isVerifiedTeacher: true,
            pdfAccess: Array.from(pdfAccess),
            ...accessUpdates,
        });
        batch.set(orderRef, { completed: true }, { merge: true })
        batch.set(mailRef, MailConverter.toFirestore(mail));
        await batch.commit();
    } catch (error) {
        console.error(error);
    }
}

const oneDay = 24 * 60 * 60;
const oneYear = 366 * oneDay;

const getValidRange = <T extends { [grade in Grade]?: AccessPeriod }>(
    accessVal: Grade,
    access: T,
    goLiveDate?: Date
  ): AccessPeriod => {
    const now = Timestamp.now();
    let validFrom = goLiveDate && now < Timestamp.fromDate(goLiveDate)
      ? Timestamp.fromDate(goLiveDate)
      : now;
  
    const currentYear = new Date().getFullYear();
    const rangeStart = Timestamp.fromDate(new Date(`${currentYear}-01-01T00:00`));
    const rangeEnd = Timestamp.fromDate(new Date(`${currentYear}-08-01T00:00`));
  
    let validTo = (validFrom > rangeStart && validFrom < rangeEnd)
      ? new Timestamp(rangeEnd.seconds + oneYear, rangeEnd.nanoseconds)
      : new Timestamp(validFrom.seconds + oneYear, validFrom.nanoseconds);
  
    const existingValidTo = access[accessVal]?.validTo;
    if (existingValidTo) {
      const extended = existingValidTo.seconds + oneYear - oneDay;
      validTo = new Timestamp(Math.max(validTo.seconds, extended), validTo.nanoseconds);
    }
  
    return { validFrom, validTo };
  };

const calcExamRange = (accessVal: Grade, examAccess: AccessMap) =>
  getValidRange(accessVal, examAccess, new Date('2025-08-14T00:00'));

const calcWpRange = (accessVal: Grade, wpAccess: AccessMap) =>
  getValidRange(accessVal, wpAccess);
